import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, ChevronLeft, Megaphone, MessageSquare, CalendarDays, Table2, Reply, X } from "lucide-react";
import { dayLabel, TZ, romeTodayKey } from "@/lib/constants";

const timeLabel = (iso) => {
  const d = new Date(iso);
  const key = (x) => x.toLocaleDateString("sv-SE", { timeZone: TZ });
  const today = key(new Date()) === key(d);
  return today ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: TZ })
    : d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: TZ });
};

export function usePolling(fn, ms, deps) {
  useEffect(() => {
    fn();
    const iv = setInterval(fn, ms);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, deps);
}

export function useUnreadMessages() {
  const [n, setN] = useState(0);
  usePolling(async () => {
    try { const r = await api.get("/messages/unread"); setN(r.data.unread); } catch (e) { /* noop */ }
  }, 10000, []);
  return n;
}

function EventTag({ m, mine, onOpenEvent }) {
  const label = `${m.event_name || "Evento"}${m.event_date ? ` · ${dayLabel(m.event_date)}` : ""}`;
  const cls = `flex items-center gap-1 text-[11px] font-semibold mb-1 rounded px-1.5 py-0.5 w-fit max-w-full ${mine ? "bg-slate-950/15 text-slate-900" : "bg-cyan-500/15 text-cyan-300"}`;
  const inner = <><CalendarDays className="w-3 h-3 shrink-0" /><span className="truncate">{label}</span></>;
  return onOpenEvent ? (
    <button type="button" data-testid={`chat-event-ref-${m.id}`} onClick={() => onOpenEvent(m.event_id)} className={`${cls} hover:underline`}>{inner}</button>
  ) : <div data-testid={`chat-event-ref-${m.id}`} className={cls}>{inner}</div>;
}

export function EventNoteButton({ eventId, eventName }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/messages/me", { text, event_id: eventId }); toast.success("Nota inviata al manager nella chat"); setText(""); setOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`event-note-btn-${eventId}`} onClick={() => setOpen(true)}
        className="mt-3 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"><MessageSquare className="w-4 h-4 mr-1.5" /> Scrivi una nota</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="font-display">Nota su: {eventName}</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Arriva al manager nella tua chat, collegata a questo evento.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="event-note-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi la tua domanda o nota…" className="bg-slate-800 border-slate-700 min-h-[110px]" />
          <DialogFooter>
            <Button onClick={send} disabled={busy || !text.trim()} data-testid="event-note-send-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
              <Send className="w-4 h-4 mr-1.5" /> Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CellTag({ m, mine }) {
  return (
    <div data-testid={`chat-cell-ref-${m.id}`} className={`text-[11px] mb-1 rounded px-1.5 py-1 w-fit max-w-full ${mine ? "bg-slate-950/15 text-slate-900" : "bg-amber-400/15 text-amber-200"}`}>
      <div className="flex items-center gap-1 font-semibold"><Table2 className="w-3 h-3 shrink-0" /><span className="truncate">{m.cell_tech_name} · {dayLabel(m.cell_date)}</span></div>
      {m.cell_text && <div className="opacity-80 truncate">{m.cell_text}</div>}
    </div>
  );
}

export function CellCommentDialog({ cell, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [cell]);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/messages/me", { text, cell_tech_id: cell.tech.id, cell_date: cell.date });
      toast.success("Messaggio inviato al manager nella chat");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!cell} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="cell-comment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Messaggio su: {cell?.tech.name} · {cell ? dayLabel(cell.date) : ""}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Arriva al manager nella chat, collegato a questa cella{cell?.text ? ` (${cell.text})` : ""}.
          </DialogDescription>
        </DialogHeader>
        <Textarea data-testid="cell-comment-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi il tuo commento…" className="bg-slate-800 border-slate-700 min-h-[110px]" />
        <DialogFooter>
          <Button onClick={send} disabled={busy || !text.trim()} data-testid="cell-comment-send-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
            <Send className="w-4 h-4 mr-1.5" /> Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const who = (fromManager, mineIsManager) => (fromManager === mineIsManager ? "Tu" : fromManager ? "Manager" : "Tecnico");

function Quote({ r, mine, mineIsManager, onJump }) {
  return (
    <button type="button" data-testid="chat-reply-quote" onClick={() => onJump(r.id)}
      className={`block w-full text-left mb-1.5 rounded-lg border-l-4 px-2 py-1 text-xs ${mine ? "bg-slate-950/15 border-slate-900/60 text-slate-900" : "bg-slate-950/40 border-cyan-400 text-slate-300"}`}>
      <span className={`block font-semibold ${mine ? "" : "text-cyan-300"}`}>{who(r.from_manager, mineIsManager)}</span>
      <span className="line-clamp-2 opacity-90">{r.text}</span>
    </button>
  );
}

function MessageList({ messages, mineIsManager, onOpenEvent, onReply }) {
  const end = useRef(null);
  const [flash, setFlash] = useState(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages.length]);
  const jump = (id) => {
    document.querySelector(`[data-testid="chat-msg-${id}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    setFlash(id); setTimeout(() => setFlash(null), 1500);
  };
  if (!messages.length) return <p className="text-sm text-slate-500 text-center py-10" data-testid="chat-empty">Nessun messaggio. Scrivi il primo!</p>;
  return (
    <div className="space-y-2" data-testid="chat-messages">
      {messages.map((m) => {
        const mine = m.from_manager === mineIsManager;
        return (
          <div key={m.id} className={`group flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`}>
            {mine && <ReplyBtn m={m} onReply={onReply} />}
            <div data-testid={`chat-msg-${m.id}`} onDoubleClick={() => onReply(m)}
              className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words transition-shadow ${flash === m.id ? "ring-2 ring-amber-400" : ""} ${mine ? "bg-cyan-500 text-slate-950 rounded-br-sm" : "bg-slate-800 text-slate-100 rounded-bl-sm"}`}>
              {m.reply_to && <Quote r={m.reply_to} mine={mine} mineIsManager={mineIsManager} onJump={jump} />}
              {m.event_id && <EventTag m={m} mine={mine} onOpenEvent={onOpenEvent} />}
              {m.cell_date && <CellTag m={m} mine={mine} />}
              {m.text}
              <div className={`text-[10px] mt-1 ${mine ? "text-slate-900/70" : "text-slate-400"}`}>
                {timeLabel(m.created_at)}{mine && m.read_at ? " · letto" : ""}
              </div>
            </div>
            {!mine && <ReplyBtn m={m} onReply={onReply} />}
          </div>
        );
      })}
      <div ref={end} />
    </div>
  );
}

function ReplyBtn({ m, onReply }) {
  return (
    <button type="button" title="Rispondi" data-testid={`chat-reply-btn-${m.id}`} onClick={() => onReply(m)}
      className="p-1.5 rounded-full text-slate-500 hover:text-cyan-300 hover:bg-slate-800 opacity-60 md:opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
      <Reply className="w-4 h-4" />
    </button>
  );
}

function Composer({ onSend, replyTo, onCancelReply, mineIsManager }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  useEffect(() => { if (replyTo) input.current?.focus(); }, [replyTo]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onSend(text, replyTo?.id); setText(""); onCancelReply(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="pt-3 border-t border-slate-800 space-y-2">
      {replyTo && (
        <div className="flex items-start gap-2 rounded-lg border-l-4 border-cyan-400 bg-slate-800/70 px-3 py-2 text-xs" data-testid="chat-replying-bar">
          <Reply className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1"><span className="font-semibold text-cyan-300">Rispondi a {who(replyTo.from_manager, mineIsManager).replace("Tu", "te stesso")}</span>
            <p className="truncate text-slate-400">{replyTo.text}</p></div>
          <button type="button" data-testid="chat-reply-cancel" onClick={onCancelReply} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
      <Textarea ref={input} data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…"
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        className="bg-slate-800 border-slate-700 min-h-[44px] max-h-32 resize-none" rows={1} />
      <Button onClick={send} disabled={busy || !text.trim()} data-testid="chat-send-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 h-11 w-11 p-0 shrink-0">
        <Send className="w-4 h-4" />
      </Button>
      </div>
    </div>
  );
}

export function TechnicianChat({ title = "Chat con il manager" }) {
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState(null);
  const load = async () => { try { const r = await api.get("/messages/me"); setMsgs(r.data); } catch (e) { /* noop */ } };
  usePolling(load, 8000, []);
  const send = async (text, reply_to_id) => { await api.post("/messages/me", { text, reply_to_id }); await load(); };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 flex flex-col h-[calc(100vh-220px)]" data-testid="technician-chat">
      <div className="flex items-center gap-2 pb-3 border-b border-slate-800">
        <MessageSquare className="w-4 h-4 text-cyan-400" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-3"><MessageList messages={msgs} mineIsManager={false} onReply={setReply} /></div>
      <Composer onSend={send} replyTo={reply} onCancelReply={() => setReply(null)} mineIsManager={false} />
    </div>
  );
}

function BroadcastDialog({ open, onOpenChange, threads, events, onSent }) {
  const [target, setTarget] = useState("all");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const body = target === "all" ? { text, technician_ids: threads.map((t) => t.tech_id) } : { text, event_id: target };
      const r = await api.post("/messages/broadcast", body);
      toast.success(`Messaggio inviato a ${r.data.sent} tecnico/i`);
      setText(""); onOpenChange(false); onSent();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">Messaggio a più tecnici</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Arriva nella chat privata di ognuno: ciascuno risponde solo a te.</DialogDescription>
        </DialogHeader>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="broadcast-target-select" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-64">
            <SelectItem value="all">Tutti i tecnici</SelectItem>
            {events.map((e) => <SelectItem key={e.id} value={e.id}>Confermati di: {e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea data-testid="broadcast-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi il messaggio…" className="bg-slate-800 border-slate-700 min-h-[110px]" />
        <DialogFooter>
          <Button onClick={send} disabled={busy || !text.trim()} data-testid="broadcast-send-btn" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
            <Send className="w-4 h-4 mr-1.5" /> Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ThreadList({ threads, active, onPick }) {
  return (
    <div className="divide-y divide-slate-800" data-testid="chat-thread-list">
      {threads.map((t) => (
        <button key={t.tech_id} data-testid={`chat-thread-${t.tech_id}`} onClick={() => onPick(t)}
          className={`w-full text-left px-3 py-3 flex items-center gap-3 transition-colors hover:bg-slate-800/60 ${active === t.tech_id ? "bg-cyan-500/10" : ""}`}>
          <div className="w-9 h-9 rounded-full bg-cyan-500/15 text-cyan-300 flex items-center justify-center text-sm font-semibold shrink-0">{t.name?.charAt(0)}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className={`truncate text-sm ${t.unread ? "font-bold text-slate-50" : "font-medium"}`}>{t.name}</span>
              {t.collaborator && <span className="shrink-0 text-[10px] font-semibold uppercase text-amber-300" data-testid={`thread-collab-label-${t.tech_id}`}>collab.</span>}
              {t.last && <span className="text-[10px] text-slate-500 shrink-0">{timeLabel(t.last.created_at)}</span>}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-xs text-slate-500">{t.last ? `${t.last.from_manager ? "Tu: " : ""}${t.last.event_name ? `[${t.last.event_name}] ` : ""}${t.last.text}` : "Nessun messaggio"}</span>
              {t.unread > 0 && <span data-testid={`chat-unread-${t.tech_id}`} className="rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold px-1.5 shrink-0">{t.unread}</span>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function ManagerMessages({ events, onOpenEvent }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [bcOpen, setBcOpen] = useState(false);
  const [reply, setReply] = useState(null);
  const loadThreads = async () => { try { const r = await api.get("/messages/threads"); setThreads(r.data); } catch (e) { /* noop */ } };
  const loadMsgs = async () => {
    if (!active) return;
    try { const r = await api.get(`/messages/thread/${active.tech_id}`); setMsgs(r.data); } catch (e) { /* noop */ }
  };
  usePolling(loadThreads, 8000, []);
  usePolling(loadMsgs, 8000, [active?.tech_id]);
  const send = async (text, reply_to_id) => { await api.post(`/messages/thread/${active.tech_id}`, { text, reply_to_id }); await loadMsgs(); loadThreads(); };
  const upcoming = (events || []).filter((e) => (e.end_date || e.start_date) >= romeTodayKey());

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden" data-testid="manager-messages">
      <div className="grid md:grid-cols-[300px_1fr] h-[calc(100vh-240px)] min-h-[420px]">
        <div className={`border-r border-slate-800 flex flex-col min-h-0 ${active ? "hidden md:flex" : "flex"}`}>
          <div className="flex items-center justify-between px-3 py-3 border-b border-slate-800">
            <h3 className="font-semibold text-sm">Chat private</h3>
            <Button size="sm" variant="outline" onClick={() => setBcOpen(true)} data-testid="broadcast-open-btn" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 h-8">
              <Megaphone className="w-3.5 h-3.5 mr-1.5" /> A più tecnici
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto"><ThreadList threads={threads} active={active?.tech_id} onPick={(t) => { setMsgs([]); setReply(null); setActive(t); }} /></div>
        </div>
        <div className={`flex-col min-h-0 p-4 ${active ? "flex" : "hidden md:flex"}`}>
          {active ? (
            <>
              <div className="flex items-center gap-2 pb-3 border-b border-slate-800">
                <button className="md:hidden text-slate-400" onClick={() => setActive(null)} data-testid="chat-back-btn"><ChevronLeft className="w-5 h-5" /></button>
                <h3 className="font-semibold" data-testid="chat-active-name">{active.name}</h3>
              </div>
              <div className="flex-1 overflow-y-auto py-3"><MessageList messages={msgs} mineIsManager={true} onOpenEvent={onOpenEvent} onReply={setReply} /></div>
              <Composer onSend={send} replyTo={reply} onCancelReply={() => setReply(null)} mineIsManager={true} />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Scegli un tecnico per aprire la chat</div>
          )}
        </div>
      </div>
      <BroadcastDialog open={bcOpen} onOpenChange={setBcOpen} threads={threads} events={upcoming} onSent={loadThreads} />
    </div>
  );
}
